import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Lock, Check } from "lucide-react";
import { apiJson } from "../lib/queryClient";
import ExpoPreviewLink from "../components/ExpoPreviewLink";
import DeletionCountdownNotice from "../components/DeletionCountdownNotice";
import {
  euro,
  formatDate,
  intervalLabel,
  intervalSuffix,
  entitlementHasCountry,
  useSubscription,
  type PackagesResponse,
  type CountryAddon,
  type Entitlement,
} from "../lib/subscription";

function sourceLabel(source: string): string {
  switch (source) {
    case "admin":
      return "Administrator";
    case "manual":
      return "Manuell freigeschaltet";
    case "stripe":
      return "Abo (Stripe)";
    case "trial":
      return "Kostenloser Test";
    default:
      return "Kein Abo";
  }
}

function statusLabel(status: string | null): string {
  switch (status) {
    case "active":
      return "Aktiv";
    case "trialing":
      return "Testphase";
    case "past_due":
      return "Zahlung überfällig";
    case "canceled":
      return "Gekündigt";
    case "manual":
      return "Manuell";
    default:
      return status ?? "—";
  }
}

function addonPrice(addon: CountryAddon) {
  return (
    addon.prices.find((p) => p.interval === "month") ?? addon.prices[0] ?? null
  );
}

/**
 * Länder-Add-ons (Task #522): zusätzliche Länder einzeln freischalten. Wird auch
 * aktiven Abonnenten angezeigt (die /pakete nicht mehr sehen). Buchung läuft über
 * /api/subscription/checkout/country → Stripe-Checkout.
 */
function CountryAddonSection({ entitlement }: { entitlement: Entitlement }) {
  const { data } = useQuery<PackagesResponse>({
    queryKey: ["/api/subscription/packages"],
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addonError, setAddonError] = useState<string | null>(null);

  const addons = data?.countryAddons ?? [];
  if (addons.length === 0) return null;

  async function unlock(addon: CountryAddon) {
    const price = addonPrice(addon);
    if (!price) return;
    setAddonError(null);
    setBusyId(addon.id);
    try {
      const res = await apiJson<{ url: string }>(
        "POST",
        "/api/subscription/checkout/country",
        { priceId: price.id },
      );
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      setAddonError("Checkout konnte nicht gestartet werden.");
    } catch (err) {
      setAddonError(
        (err as Error).message || "Checkout konnte nicht gestartet werden.",
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card p-5" data-testid="card-country-addons">
      <h2 className="text-[17px] font-bold text-[var(--color-app-text)]">
        Weitere Länder freischalten
      </h2>
      <p className="mt-1 text-[13px] text-[var(--color-app-text-secondary)]">
        Dein Abo enthält ein Land. Zusätzliche Länder kannst du hier einzeln
        dazubuchen.
      </p>

      {addonError ? (
        <div className="mt-3 text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2">
          {addonError}
        </div>
      ) : null}

      <ul className="mt-4 space-y-2.5">
        {addons.map((addon) => {
          const owned = entitlementHasCountry(entitlement, addon.country);
          const price = addonPrice(addon);
          return (
            <li
              key={addon.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-app-border)] px-3 py-2.5"
              data-testid={`row-country-addon-${addon.country}`}
            >
              <div className="min-w-0">
                <div className="font-semibold text-[14px] text-[var(--color-app-text)]">
                  {addon.name}
                </div>
                {price ? (
                  <div className="text-[12px] text-[var(--color-app-text-secondary)]">
                    {euro(price.unitAmount, price.currency)}{" "}
                    {intervalSuffix(price.interval)}
                  </div>
                ) : null}
              </div>
              {owned ? (
                <span
                  className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full bg-[var(--color-go-bg)] text-[var(--color-go)]"
                  data-testid={`badge-owned-${addon.country}`}
                >
                  <Check size={13} />
                  Freigeschaltet
                </span>
              ) : (
                <button
                  className="btn-primary text-[13px] px-3 py-1.5 inline-flex items-center gap-1.5 shrink-0"
                  onClick={() => unlock(addon)}
                  disabled={busyId === addon.id || !price}
                  data-testid={`button-unlock-${addon.country}`}
                >
                  <Lock size={13} />
                  {busyId === addon.id ? "Wird geöffnet …" : "Freischalten"}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Subscription() {
  const { data, isLoading, isError, error } = useSubscription();
  const [portalBusy, setPortalBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function openPortal() {
    setActionError(null);
    setPortalBusy(true);
    try {
      const res = await apiJson<{ url: string }>(
        "POST",
        "/api/subscription/portal",
      );
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      setActionError("Portal konnte nicht geöffnet werden.");
    } catch (err) {
      setActionError((err as Error).message || "Portal konnte nicht geöffnet werden.");
    } finally {
      setPortalBusy(false);
    }
  }

  if (isLoading) {
    return (
      <div className="auth-shell auth-embed max-w-xl mx-auto">
        <div className="py-16 text-center text-sm text-[var(--color-app-text-tertiary)]">
          Abo-Status wird geladen …
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="auth-shell auth-embed max-w-xl mx-auto">
        <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2">
          {(error as Error)?.message || "Abo-Status konnte nicht geladen werden."}
        </div>
      </div>
    );
  }

  const ent = data!.entitlement;
  const trial = data!.trial;
  const isStripe = ent.source === "stripe";

  return (
    <div className="auth-shell auth-embed space-y-4 max-w-xl mx-auto">
      <DeletionCountdownNotice />
      <div className="card p-5" data-testid="card-subscription-status">
        <div className="flex items-center justify-between">
          <h2 className="text-[17px] font-bold text-[var(--color-app-text)]">
            Aktueller Status
          </h2>
          <span
            className={`text-[12px] font-semibold px-2.5 py-1 rounded-full ${
              ent.active
                ? "bg-[var(--color-go-bg)] text-[var(--color-go)]"
                : "bg-[var(--color-nogo-bg)] text-[var(--color-nogo)]"
            }`}
            data-testid="badge-active"
          >
            {ent.active ? "Aktiv" : "Inaktiv"}
          </span>
        </div>

        <dl className="mt-4 space-y-2.5 text-[14px]">
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--color-app-text-secondary)]">Quelle</dt>
            <dd className="font-semibold text-[var(--color-app-text)] text-right">
              {sourceLabel(ent.source)}
            </dd>
          </div>

          {ent.plan?.productName ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-app-text-secondary)]">Paket</dt>
              <dd className="font-semibold text-[var(--color-app-text)] text-right">
                {ent.plan.productName}
              </dd>
            </div>
          ) : null}

          {ent.plan?.amount != null ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-app-text-secondary)]">Preis</dt>
              <dd className="font-semibold text-[var(--color-app-text)] text-right">
                {euro(ent.plan.amount, ent.plan.currency ?? "eur")}{" "}
                {intervalLabel(ent.plan.interval)}
              </dd>
            </div>
          ) : null}

          {ent.status ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-app-text-secondary)]">Abo-Status</dt>
              <dd className="font-semibold text-[var(--color-app-text)] text-right">
                {statusLabel(ent.status)}
              </dd>
            </div>
          ) : null}

          {isStripe && ent.currentPeriodEnd ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-app-text-secondary)]">
                {ent.cancelAtPeriodEnd ? "Läuft ab am" : "Verlängert sich am"}
              </dt>
              <dd className="font-semibold text-[var(--color-app-text)] text-right">
                {formatDate(ent.currentPeriodEnd)}
              </dd>
            </div>
          ) : null}

          {ent.source === "trial" && trial.expiresAt ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-app-text-secondary)]">Test endet am</dt>
              <dd className="font-semibold text-[var(--color-app-text)] text-right">
                {formatDate(trial.expiresAt)}
              </dd>
            </div>
          ) : null}

          {ent.manualPlanUntil ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--color-app-text-secondary)]">
                Freigeschaltet bis
              </dt>
              <dd className="font-semibold text-[var(--color-app-text)] text-right">
                {formatDate(ent.manualPlanUntil)}
              </dd>
            </div>
          ) : null}
        </dl>

        {isStripe && ent.cancelAtPeriodEnd ? (
          <p className="mt-3 text-[13px] text-[var(--color-app-text-secondary)]">
            Dein Abo wurde gekündigt und endet zum oben genannten Datum.
          </p>
        ) : null}
      </div>

      {ent.active && !ent.allCountries ? (
        <CountryAddonSection entitlement={ent} />
      ) : null}

      {actionError ? (
        <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2">
          {actionError}
        </div>
      ) : null}

      {isStripe ? (
        <button
          className="btn-primary w-full inline-flex items-center justify-center gap-2"
          onClick={openPortal}
          disabled={portalBusy}
          data-testid="button-portal"
        >
          <ExternalLink size={16} />
          {portalBusy ? "Wird geöffnet …" : "Abo verwalten (Stripe)"}
        </button>
      ) : (
        <Link
          to="/pakete"
          className="btn-primary w-full inline-flex items-center justify-center"
          data-testid="link-packages"
        >
          {ent.active ? "Paket wechseln" : "Paket wählen"}
        </Link>
      )}

      <ExpoPreviewLink />

      <p className="text-center text-[12px] text-[var(--color-app-text-tertiary)] pt-2">
        Zahlung & Verwaltung erfolgen sicher über Stripe.
      </p>
    </div>
  );
}
